import { useEffect, useRef } from 'react';
import { allImages } from '../lib/image';
import type { Word, BuscarScene, Cuento, JwHistoria, JwComic, JwVideo, LetterSounds } from '../types';

// Audio que PUEDE ser pesado (los previews de iTunes pesan 1 MB o más). En
// iOS se le pide el tamaño antes de bajarlo y se saltea lo que pase el tope,
// para no tumbar el proceso de WebKit por memoria. Antes se salteaba por
// extensión, y con eso quedaban afuera también las grabaciones del estudio
// (.wav de 15 a 310 KB): sin conexión, en el iPad sonaba el TTS aunque
// hubiera grabación.
const HEAVY_AUDIO_RE = /\.(mp3|m4a|aac|wav|ogg)(\?|$)/i;
const MAX_IOS_PRECACHE_AUDIO_BYTES = 768 * 1024;

// Videos are expected to stay small (uploader is asked to compress heavily —
// see AdminVideosJW's ~95MB upload ceiling), but that's still far bigger than
// anything else this hook precaches. On iOS we HEAD-check each video's real
// size first and skip proactively caching anything over this before
// committing to a full background download.
const MAX_IOS_PRECACHE_VIDEO_BYTES = 25 * 1024 * 1024;

const isIOS =
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !(navigator as { userAgent: string } & { MSStream?: unknown }).MSStream;

function collectUrls(
  items: Word[],
  scenes: BuscarScene[],
  cuentos: Cuento[],
  jwHistorias: JwHistoria[],
  jwComics: JwComic[],
  jwVideos: JwVideo[],
  letterSounds: LetterSounds
): { urls: string[]; videoUrls: string[] } {
  const urls = new Set<string>();
  const videoUrls = new Set<string>();

  for (const item of items) {
    for (const img of allImages(item)) if (img) urls.add(img);
    if (item.sound) urls.add(item.sound);
    if (Array.isArray(item.sounds)) {
      for (const s of item.sounds) if (s) urls.add(s);
    }
  }

  for (const scene of scenes)  { if (scene.image) urls.add(scene.image); }

  for (const cuento of cuentos) {
    if (cuento.cover) urls.add(cuento.cover);
    for (const page of cuento.pages ?? []) { if (page.image) urls.add(page.image); }
  }

  for (const historia of jwHistorias) {
    if (historia.cover) urls.add(historia.cover);
    for (const page of historia.pages ?? []) { if (page.image) urls.add(page.image); }
  }

  for (const comic of jwComics) {
    if (comic.cover) urls.add(comic.cover);
    for (const page of comic.pages ?? []) { if (page.image) urls.add(page.image); }
  }

  for (const video of jwVideos) {
    if (video.thumbnail) urls.add(video.thumbnail);
    if (video.video) videoUrls.add(video.video);
  }

  for (const url of Object.values(letterSounds)) { if (url) urls.add(url); }

  const all = [...urls].filter((u) => u.startsWith('/') || u.startsWith('http'));
  const allVideos = [...videoUrls].filter((u) => u.startsWith('/') || u.startsWith('http'));

  return { urls: all, videoUrls: allVideos };
}

/** ¿Pesa más que `max`? Sin tamaño informado (o sin red) se da por bueno. */
async function tooBig(url: string, max: number): Promise<boolean> {
  const head = await fetch(url, { method: 'HEAD', credentials: 'same-origin' }).catch(() => null);
  const len = head?.headers.get('Content-Length');
  return Boolean(len && parseInt(len, 10) > max);
}

async function fetchAll(urls: string[], signal: AbortSignal): Promise<void> {
  // iOS needs single-file sequential fetching with breathing room.
  const BATCH = isIOS ? 1 : 4;
  const DELAY = isIOS ? 600 : 150;

  for (let i = 0; i < urls.length; i += BATCH) {
    if (signal.aborted) return;
    await Promise.allSettled(
      urls.slice(i, i + BATCH).map(async (url) => {
        try {
          const cached = await caches.match(url);
          if (cached) return; // already in cache, skip network
          if (isIOS && HEAVY_AUDIO_RE.test(url) && (await tooBig(url, MAX_IOS_PRECACHE_AUDIO_BYTES))) return;
          await fetch(url, { credentials: 'same-origin' });
        } catch {
          // offline or asset missing — silently ignore
        }
      })
    );
    await new Promise<void>((r) => setTimeout(r, DELAY));
  }
}

// Videos are the heaviest thing this hook ever fetches, so they're always
// downloaded one at a time (regardless of platform) with a size gate on iOS
// specifically — a plain GET populates the same `clarisa-uploads` Workbox
// cache (CacheFirst, see vite.config.ts) that <video> playback reads from.
async function fetchVideos(urls: string[], signal: AbortSignal): Promise<void> {
  for (const url of urls) {
    if (signal.aborted) return;
    try {
      const cached = await caches.match(url);
      if (cached) continue;

      if (isIOS && (await tooBig(url, MAX_IOS_PRECACHE_VIDEO_BYTES))) continue; // too big — play on-demand instead

      await fetch(url, { credentials: 'same-origin' });
    } catch {
      // offline or asset missing — silently ignore
    }
    await new Promise<void>((r) => setTimeout(r, isIOS ? 800 : 200));
  }
}

export function useBackgroundPrecache(
  items: Word[],
  scenes: BuscarScene[],
  cuentos: Cuento[],
  jwHistorias: JwHistoria[],
  jwComics: JwComic[],
  jwVideos: JwVideo[],
  letterSounds: LetterSounds,
  loading: boolean
): void {
  // Keep a ref to the latest data so the effect closure doesn't go stale
  const dataRef = useRef({ items, scenes, cuentos, jwHistorias, jwComics, jwVideos, letterSounds });
  dataRef.current = { items, scenes, cuentos, jwHistorias, jwComics, jwVideos, letterSounds };

  // Rate-limit: only re-run when the NUMBER of items changes (new content uploaded).
  // Using a stable string avoids triggering on same-content array re-renders.
  const dataKey = `${items.length}-${scenes.length}-${cuentos.length}-${jwHistorias.length}-${jwComics.length}-${jwVideos.length}-${Object.keys(letterSounds).length}`;
  const lastKeyRef = useRef('');

  useEffect(() => {
    if (loading || items.length === 0) return;
    if (dataKey === lastKeyRef.current) return; // same data, skip
    if (!navigator.serviceWorker?.controller) return;

    lastKeyRef.current = dataKey;

    const { items: i, scenes: s, cuentos: c, jwHistorias: jh, jwComics: jc, jwVideos: jv, letterSounds: l } = dataRef.current;
    const { urls, videoUrls } = collectUrls(i, s, c, jh, jc, jv, l);
    if (urls.length === 0 && videoUrls.length === 0) return;

    const controller = new AbortController();

    // On iOS prefer a plain setTimeout — requestIdleCallback fires during
    // animations/touch events on iOS and contributes to memory spikes.
    const delay = isIOS ? 3000 : 1000;
    const id = setTimeout(() => {
      void fetchAll(urls, controller.signal).then(() => fetchVideos(videoUrls, controller.signal));
    }, delay);

    return () => {
      controller.abort();
      clearTimeout(id);
    };
  // dataKey is a stable primitive — safe as the only data-change trigger
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, dataKey]);
}
